// Cart Events System — refresh-only (state sync removed, handled by CartProvider context)
// Used by components outside the CartProvider tree (e.g. GlobalQuickCheckout in root layout)

type CartEventListener = () => void;

class OptimizedCartEvents {
  private listeners = new Set<CartEventListener>();
  private refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_DEBOUNCE_MS = 50;
  private isRefreshing = false;

  onCartRefresh(listener: CartEventListener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  emitCartRefresh() {
    if (this.isRefreshing) return;
    if (this.refreshDebounceTimer) clearTimeout(this.refreshDebounceTimer);
    this.refreshDebounceTimer = setTimeout(() => {
      this.executeCartRefresh();
    }, this.REFRESH_DEBOUNCE_MS);
  }

  private executeCartRefresh() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    try {
      this.listeners.forEach(listener => {
        try { listener(); } catch (error) {
          console.error('[CART EVENTS] Error in cart refresh listener:', error);
        }
      });
    } finally {
      this.isRefreshing = false;
    }
  }

  forceRefresh() {
    if (this.refreshDebounceTimer) {
      clearTimeout(this.refreshDebounceTimer);
      this.refreshDebounceTimer = null;
    }
    this.executeCartRefresh();
  }
}

export const optimizedCartEvents = new OptimizedCartEvents();

export const cartEvents = {
  onCartRefresh: (listener: CartEventListener) => optimizedCartEvents.onCartRefresh(listener),
  emitCartRefresh: () => optimizedCartEvents.emitCartRefresh(),
  forceRefresh: () => optimizedCartEvents.forceRefresh(),
};
